/**
 * Rivers level-of-detail (LOD) — zoom-dependent two-tier detail for the
 * clip-to-region rivers layer.
 *
 * The problem (CL-109 follow-up): reading HydroRIVERS by a mega-basin's whole
 * bounding box (Amazon/Congo/Nile-class) returns hundreds of thousands of
 * reaches — a multi-MB range read plus a slow geolibre clip. The fix is two
 * tiers of data and a viewport-reactive loader:
 *
 *   - COARSE tier (hydrorivers_coarse.fgb, ORD_FLOW ≤ 3): major rivers only.
 *     Cheap to read even across a continent. Shown when zoomed out.
 *   - DETAIL tier (hydrorivers.fgb, ORD_FLOW ≤ 5): the full network. Read only
 *     for the CURRENT viewport once the user has zoomed in, so the reach count
 *     (and the clip) stay bounded no matter how big the country is.
 *
 * Small regions (below AREA_SMALL_REGION deg²) never need this — they render the
 * detail tier once. Only large regions get a controller that swaps coarse↔detail
 * on `moveend`.
 *
 * This module holds the pure decision logic (exported + unit-tested) and a
 * RiverLod controller whose collaborators (map, feature loader, clip, render)
 * are all injected, so the whole thing is testable without a live map.
 */

// ── Tunable thresholds (deg² and zoom) ──────────────────────────────
// A region whose bbox is smaller than this renders the detail tier in one shot,
// no controller. ~Kenya (82 deg²) and Egypt (~99) fall below; Brazil (~1560),
// DRC, India, Argentina fall above.
export const AREA_SMALL_REGION = 300

// At/above this map zoom the user has "zoomed in" enough to warrant detail for
// the current viewport; below it we show the coarse overview of the whole region.
export const ZOOM_DETAIL = 5.5

// Debounce for moveend so a fling/animation doesn't fire many reads.
export const MOVE_DEBOUNCE_MS = 350

// Rounding (decimal degrees) for the dedup key so sub-pixel jitters don't reload.
const KEY_PRECISION = 1

// ── Pure geometry helpers ───────────────────────────────────────────

/** Area of a bbox in square degrees (0 for degenerate/nullish). Pure. */
export function bboxArea(b) {
  if (!b) return 0
  const w = b.maxX - b.minX
  const h = b.maxY - b.minY
  return w > 0 && h > 0 ? w * h : 0
}

/** Intersection of two bboxes, or null if they don't overlap. Pure. */
export function intersectBbox(a, b) {
  if (!a || !b) return null
  const minX = Math.max(a.minX, b.minX)
  const minY = Math.max(a.minY, b.minY)
  const maxX = Math.min(a.maxX, b.maxX)
  const maxY = Math.min(a.maxY, b.maxY)
  if (minX >= maxX || minY >= maxY) return null
  return { minX, minY, maxX, maxY }
}

/**
 * Normalize a MapLibre LngLatBounds (or a {west,south,east,north} / array form)
 * to a plain {minX,minY,maxX,maxY}. Pure — accepts anything with the getters or
 * the fields so tests can pass a plain object.
 */
export function boundsToBbox(bounds) {
  if (!bounds) return null
  if (typeof bounds.getWest === 'function') {
    return {
      minX: bounds.getWest(),
      minY: bounds.getSouth(),
      maxX: bounds.getEast(),
      maxY: bounds.getNorth(),
    }
  }
  if (Array.isArray(bounds) && bounds.length === 4) {
    const [minX, minY, maxX, maxY] = bounds
    return { minX, minY, maxX, maxY }
  }
  const { west, south, east, north, minX, minY, maxX, maxY } = bounds
  if ([west, south, east, north].every((v) => typeof v === 'number')) {
    return { minX: west, minY: south, maxX: east, maxY: north }
  }
  if ([minX, minY, maxX, maxY].every((v) => typeof v === 'number')) {
    return { minX, minY, maxX, maxY }
  }
  return null
}

/** Which tier the initial render should use for a region of this bbox. Pure. */
export function chooseInitialTier(regionBbox, opts = {}) {
  const areaSmall = opts.areaSmallRegion ?? AREA_SMALL_REGION
  return bboxArea(regionBbox) < areaSmall ? 'detail' : 'coarse'
}

/** Whether a large region should show detail at this zoom. Pure. */
export function desiredTierForZoom(zoom, opts = {}) {
  const zoomDetail = opts.zoomDetail ?? ZOOM_DETAIL
  return Number(zoom) >= zoomDetail ? 'detail' : 'coarse'
}

/**
 * The bbox to READ for a tier: coarse reads the whole region (few major reaches);
 * detail reads only the visible viewport ∩ region, so the read stays small no
 * matter how large the region is. Returns null when they don't overlap. Pure.
 */
export function readBboxForTier(tier, regionBbox, viewportBbox) {
  if (tier === 'coarse') return regionBbox || null
  return intersectBbox(regionBbox, viewportBbox)
}

/** A stable key for a (tier, bbox) so we skip redundant reloads. Pure. */
export function tierKey(tier, bbox, precision = KEY_PRECISION) {
  if (!bbox) return `${tier}:none`
  const r = (n) => Number(n).toFixed(precision)
  return `${tier}:${r(bbox.minX)},${r(bbox.minY)},${r(bbox.maxX)},${r(bbox.maxY)}`
}

// ── Controller ──────────────────────────────────────────────────────

// One active controller at a time — a new clip (or teardown) replaces it.
let _active = null

/** Tear down whichever river-LOD controller is currently active (if any). */
export function teardownActiveRiverLod() {
  if (_active) {
    _active.teardown()
    _active = null
  }
}

/**
 * Start (and register as active) a zoom-dependent river LOD controller for a
 * large region. Tears down any previous one first. Returns the controller.
 *
 * Collaborators (injected):
 *   map            — { getZoom(), getBounds(), on(ev,fn), off(ev,fn) }
 *   regionFc       — clip polygon FeatureCollection
 *   regionBbox     — precomputed bbox of regionFc
 *   isLayerAlive   — () => boolean; false once another command cleared the rivers
 *                    layer, which self-tears-down the controller
 *   loadTier       — async (tier, readBbox) => feature[]  (reads the right .fgb)
 *   clipToRegion   — async (features, regionFc) => clipped FeatureCollection
 *   render         — (clippedFC, { tier }) => void  (draws the layer)
 *   opts           — { zoomDetail, moveDebounceMs, setTimeoutFn, clearTimeoutFn }
 */
export function startRiverLod(config) {
  teardownActiveRiverLod()
  const c = new RiverLod(config)
  _active = c
  c.start()
  return c
}

export class RiverLod {
  constructor({
    map,
    regionFc,
    regionBbox,
    isLayerAlive,
    loadTier,
    clipToRegion,
    render,
    opts = {},
  }) {
    this.map = map
    this.regionFc = regionFc
    this.regionBbox = regionBbox
    this.isLayerAlive = isLayerAlive || (() => true)
    this.loadTier = loadTier
    this.clipToRegion = clipToRegion
    this.render = render
    this.zoomDetail = opts.zoomDetail ?? ZOOM_DETAIL
    this.moveDebounceMs = opts.moveDebounceMs ?? MOVE_DEBOUNCE_MS
    this._setTimeout = opts.setTimeoutFn || ((fn, ms) => setTimeout(fn, ms))
    this._clearTimeout = opts.clearTimeoutFn || ((h) => clearTimeout(h))

    this._lastKey = null
    this._busy = false
    this._debounceHandle = null
    this._torn = false
    this._onMove = () => this._schedule()
  }

  /** Attach the moveend listener. The initial coarse render is done by the caller
   *  (execute) so the first paint isn't delayed by controller setup. */
  start() {
    // Seed the dedup key with the initial coarse render of the whole region so we
    // don't immediately re-render it on the first moveend.
    this._lastKey = tierKey('coarse', this.regionBbox)
    this.map?.on?.('moveend', this._onMove)
  }

  teardown() {
    this._torn = true
    if (this._debounceHandle != null) this._clearTimeout(this._debounceHandle)
    this._debounceHandle = null
    this.map?.off?.('moveend', this._onMove)
  }

  _schedule() {
    if (this._torn) return
    if (this._debounceHandle != null) this._clearTimeout(this._debounceHandle)
    this._debounceHandle = this._setTimeout(() => {
      this._debounceHandle = null
      this.evaluate()
    }, this.moveDebounceMs)
  }

  /**
   * Decide the tier for the current view and (re)load+clip+render if it changed.
   * Exported behavior is pure-ish: it reads map.getZoom()/getBounds(), then uses
   * the injected loadTier/clipToRegion/render. Safe to call directly in tests.
   */
  async evaluate() {
    if (this._torn) return
    // Self-teardown: another command cleared the rivers layer.
    if (!this.isLayerAlive()) {
      this.teardown()
      if (_active === this) _active = null
      return
    }
    if (this._busy) return

    const zoom = this.map?.getZoom?.() ?? 0
    const tier = desiredTierForZoom(zoom, { zoomDetail: this.zoomDetail })
    const viewportBbox = boundsToBbox(this.map?.getBounds?.())
    const readBbox = readBboxForTier(tier, this.regionBbox, viewportBbox)
    if (!readBbox) return

    const key = tierKey(tier, tier === 'coarse' ? this.regionBbox : readBbox)
    if (key === this._lastKey) return

    this._busy = true
    try {
      const features = await this.loadTier(tier, readBbox)
      if (this._torn || !this.isLayerAlive()) return
      if (!Array.isArray(features) || !features.length) return
      const clipped = await this.clipToRegion(features, this.regionFc)
      if (this._torn || !this.isLayerAlive()) return
      if (!clipped?.features?.length) return
      this.render(clipped, { tier })
      this._lastKey = key
    } finally {
      this._busy = false
    }
  }
}
